import React, { lazy, Suspense, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { Agentation } from 'agentation';
import ReactGA from 'react-ga4';
import HomePage from './pages/HomePage';
import ProjectDetailRoute from './pages/ProjectDetailRoute';
import PageTransition from './components/layout/PageTransition';
import InterviewGeniePage from './components/InterviewGeniePage';
import TimeToGoPrivacy from './components/TimeToGoPrivacy';
import TimeToGoInfo from './components/TimeToGoInfo';
import TimeToGoMacTerms from './components/TimeToGoMacTerms';
import TokiPage from './components/TokiPage';
import { EchoPrivacy, EchoSupport } from './components/EchoPages';

const UaePublicHolidaysPage = lazy(() => import('./pages/UaePublicHolidaysPage'));

const CareersPage = lazy(() => import('./pages/CareersPage'));

if (process.env.NODE_ENV === 'production') {
  ReactGA.initialize('G-TDK5XHSPG5');
}

const PageTracker: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const lastScrollTarget = useRef<string | undefined>(undefined);

  useEffect(() => {
    const scrollTarget = `${location.pathname}${location.hash}`;
    if (lastScrollTarget.current !== scrollTarget) {
      if (location.hash) {
        window.requestAnimationFrame(() => document.getElementById(location.hash.slice(1))?.scrollIntoView());
      } else {
        window.scrollTo(0, 0);
      }
      lastScrollTarget.current = scrollTarget;
    }

    if (process.env.NODE_ENV === 'production') {
      ReactGA.send({ hitType: 'pageview', page: `${location.pathname}${location.search}${location.hash}` });
    }
  }, [location.hash, location.pathname, location.search]);

  return <>{children}</>;
};

const AnimatedRoutes: React.FC = () => {
  const location = useLocation();
  const page = (node: React.ReactNode) => <PageTransition>{node}</PageTransition>;

  return (
    <PageTracker>
      <AnimatePresence mode="wait">
        <Routes location={location} key={location.pathname}>
          <Route path="/" element={page(<HomePage />)} />
          <Route path="/uae-public-holidays" element={page(<Suspense fallback={<main aria-busy="true">Loading holidays…</main>}><UaePublicHolidaysPage /></Suspense>)} />
          <Route path="/careers" element={page(<Suspense fallback={<main aria-busy="true">Loading careers…</main>}><CareersPage /></Suspense>)} />
          <Route path="/projects/:slug" element={page(<ProjectDetailRoute />)} />
          <Route path="/interview-genie" element={page(<InterviewGeniePage />)} />
          <Route path="/timetogo/privacy" element={page(<TimeToGoPrivacy />)} />
          <Route path="/timetogo/info" element={page(<TimeToGoInfo />)} />
          <Route path="/timetogo/macapp/terms" element={page(<TimeToGoMacTerms />)} />
          <Route path="/toki" element={page(<TokiPage />)} />
          <Route path="/echo/support" element={page(<EchoSupport />)} />
          <Route path="/echo/privacy" element={page(<EchoPrivacy />)} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AnimatePresence>
    </PageTracker>
  );
};

function App() {
  return (
    <>
      <Router>
        <AnimatedRoutes />
      </Router>
      {process.env.NODE_ENV === 'development' ? <Agentation /> : null}
    </>
  );
}

export default App;
